// Admin-panel event names (GIS-215), per Gist/documents/analytics-taxonomy.md §6a.
//
// WHY THESE ARE ALL PREFIXED `admin_`, AND WHY THAT IS NOT DECORATION
//
// The v1 taxonomy is twenty names describing a MEMBER funnel, and §2 makes event
// names a contract across five codebases. Decision 11 originally kept this panel
// uninstrumented partly to avoid inventing a second vocabulary next to that one.
// Instrumenting it means the second vocabulary now exists, so the job is to make
// the two impossible to confuse:
//
//   - The `admin_` prefix means no name here can ever collide with a member
//     event, and every insight built on the member funnel stays correct without
//     anyone remembering to exclude this surface.
//   - `surface: admin` is on every event as well (§3). The prefix is not a
//     substitute for it — the prefix protects the NAME space, the property
//     answers "where did this come from" for the events that do share names,
//     which is `$pageview` and nothing else.
//
// These names describe OPERATOR/HOST actions in a back office. They are not
// product usage and must never be mixed into activation, conversion or retention
// metrics. Decision 4 set that boundary for `otp_requested`; this list extends
// it to a whole vocabulary.
//
// Change a value here only by changing the taxonomy doc first.

export const EVENTS = {
  // --- Event creation funnel -------------------------------------------
  // The reason this surface got instrumented at all. Decision 11 named panel
  // abandonment as the one host-side question server events genuinely cannot
  // answer: `event_created` fires from `onEventUpdate`, so it only ever sees
  // the creates that LANDED. A host who fills in half a form and leaves is
  // invisible to every server event we have.
  EVENT_DRAFT_STARTED: "admin_event_draft_started",
  EVENT_CREATE_SUBMITTED: "admin_event_create_submitted",
  EVENT_CREATE_FAILED: "admin_event_create_failed",

  // --- Everything else a host does -------------------------------------
  EVENT_LIST_VIEWED: "admin_event_list_viewed",
  SUGGESTION_CONVERTED: "admin_suggestion_converted",
  COUPON_CREATED: "admin_coupon_created",
  HOST_PROFILE_SAVED: "admin_host_profile_saved",

  // --- The nudge to plan the next few months --------------------------
  // A host acting on the ideas drafted for them: which kind of idea they
  // took, whether they asked for a new set, and whether they turned the
  // texts off. Properties carry no free text, only the shape of the idea.
  HOST_PLAN_IDEA_CLICKED: "admin_host_plan_idea_clicked",
  HOST_PLAN_REFRESHED: "admin_host_plan_refreshed",
  HOST_NUDGE_OPT_OUT: "admin_host_nudge_opt_out",

  // --- Diagnostics (GIS-182) -------------------------------------------
  // Not a host action. An image asked for a WebP variant, errored, and fell
  // back to the full-size original, which looks fine and costs egress. Once
  // per original path per page load; storage prefix, size and extension only.
  IMAGE_VARIANT_FALLBACK: "admin_image_variant_fallback",
};

// Deliberately NOT fired from this surface, with the reason, so a future reader
// does not "fix" a gap that is a decision:
//
//   event_created        server (`onEventUpdate`). The panel cannot see region,
//                        and it can drop a create that actually landed — the doc
//                        write succeeds, then photo upload throws into a catch
//                        that shows "Failed to save event". That is precisely
//                        why `admin_event_create_submitted` is named for the
//                        SUBMIT and not for the create.
//   event_cancelled      server (`cancelEvent` callable), including the panel's
//                        own CancelEventWizard, which calls it.
//   payout_received      server.
//   otp_requested,       decision 4: operator logins are not member activation,
//   otp_verified         and this panel's users are the platform's most frequent
//                        sign-ins.
//   profile_completed    member-funnel event with a fixed meaning. A host
//                        finishing /profile-setup here is a different thing;
//                        it reports as `admin_host_profile_saved` instead.
export const NOT_OURS = Object.freeze([
  "event_created",
  "event_cancelled",
  "payout_received",
  "otp_requested",
  "otp_verified",
  "profile_completed",
  "ticket_purchased",
  "ticket_refunded",
]);
